import React from "react";
import { ClickAwayListener, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import styled from "styled-components";
import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import {
    ValueRuleCell,
    ValueRuleCellType,
} from "../../../../rule-engine-core/Rule/Table/Value/Structure/ValueRuleCell";
import { ValueTableRuleEditContext } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableRuleEditContext";
import { CloseGrayIcon } from "../../../../CommonUI/Icon/CloseGrayIcon";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { UIStyleConstants } from "../../../../rule-engine-core/Common/UIStyleConstants";

interface ValueTableRuleCellSettingProps {
    editContext: ValueTableRuleEditContext;
    ruleDefinition: ValueTableConfiguration;
    cell: ValueRuleCell;
}

export const ValueTableRuleCellSetting = (props: ValueTableRuleCellSettingProps) => {
    const { editContext, cell } = props;

    const closeCellSettings = () => {
        editContext.selectedCellForSettings = undefined;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let type = event.target.value as ValueRuleCellType;
        cell.cellType = type;
        closeCellSettings();
        StateManager.propertyChanged(cell, "values");
    };

    return (
        <ObserveState
            listenTo={editContext}
            properties={["selectedCellForSettings"]}
            control={() => {
                if (editContext.selectedCellForSettings !== cell) return null;

                return (
                    <ValueTableRuleCellSettingDiv>
                        <ClickAwayListener onClickAway={closeCellSettings}>
                            <Wrapper>
                                <Typography
                                    variant="body1"
                                    classes={{ root: "CellSetting-body" }}
                                >
                                    Cell Settings
                                </Typography>
                                <ClosedButton
                                    onClick={() => {
                                        editContext.selectedCellForSettings = undefined;
                                    }}
                                >
                                    <CloseGrayIcon />
                                </ClosedButton>
                                <div className="radio-wrapper">
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-label="gender"
                                            name="cellType"
                                            value={cell.cellType}
                                            onChange={handleChange}
                                        >
                                            <FormControlLabel
                                                value={ValueRuleCellType.SINGLE}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Single parameter"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value={ValueRuleCellType.MULTIPLE}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Multiple parameters"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value={ValueRuleCellType.IGNORE}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Any value"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                            <FormControlLabel
                                                value={ValueRuleCellType.MISSING}
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                    />
                                                }
                                                label="Value is missing"
                                                classes={{ root: "CellSetting-radioLabel" }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                    <Typography
                                        variant="body1"
                                        classes={{ root: "CellSetting-description" }}
                                    >
                                        To create a new parameter, press "Enter" in the cell field, and a new variable
                                        will be created which you can set the parameter.
                                    </Typography>
                                </div>
                            </Wrapper>
                        </ClickAwayListener>
                    </ValueTableRuleCellSettingDiv>
                );
            }}
        />
    );
};

const ValueTableRuleCellSettingDiv = styled.div`
  position: absolute;
  top: 5px;
  left: -5px;
  background: ${UIStyleConstants.WHITE_COLOR};
  border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
  box-sizing: border-box;
  box-shadow: 0px 6px 8px -2px  ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
  border-radius: 4px;
  z-index: 2;

  .CellSetting-body {
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    margin-bottom: 18px;
  }

  .CellSetting-radioLabel .MuiTypography-body1 {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .CellSetting-description {
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0em;
    text-align: left;
    margin-top: 18px;
    color ${UIStyleConstants.GRAY_COLOR};;
  }
`;

const Wrapper = styled.div`
    position: relative;
    padding: 16px;
    width: 220px;
`;

const ClosedButton = styled.div<{ hide?: boolean; right?: number }>`
    width: 24px;
    height: 24px;
    position: absolute;
    right: 4px;
    top: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;
